import React, {
  useState,
  useRef,
  useMemo,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Box,
  Typography,
  Chip,
  Paper,
  ClickAwayListener,
  Popper,
  Fade,
  styled,
  Grid as GridBase,
  Button,
  Alert,
  Stack,
  CircularProgress,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { getChannelColor, processAndSortChannels } from "@/helpers/utils";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { TagChip } from "../muiCustom/chip/TagChip";
import { FOCUS_BORDER } from "@/components/muiCustom/styles/focusBorder";

type FilterItem = {
  id?: string | number;
  name?: string;
  channel_name?: string;
  tag_name?: string;
  image?: string | number;
  color_code?: string;
  colorCode?: string;
  [key: string]: unknown;
};

type ChannelSelection = FilterItem | string | number;

type FilterState = {
  channels?: ChannelSelection[];
  tags?: FilterItem[];
};

type FilterDropdownProps = {
  channelsList?: FilterItem[];
  tagsList?: FilterItem[];
  onApplyFilters?: (
    _filterType: "channels" | "tags",
    _filters?: ChannelSelection[] | FilterItem[]
  ) => void;
  filters?: FilterState;
  isLoading?: boolean;
};

export type FilterDropdownRef = {
  handleRemoveSelectedFilter: (_filter: FilterItem) => void;
};

type FilterChipStyleProps = {
  selected?: boolean;
  colorCode?: string;
};

const Grid = GridBase as React.ComponentType<any>;
const EMPTY_CHANNELS: ChannelSelection[] = [];
const EMPTY_TAGS: FilterItem[] = [];

const isFilterItem = (item: ChannelSelection): item is FilterItem =>
  typeof item === "object" && item !== null;

const FilterInput = styled(Box)(({ theme }) => ({
  borderRadius: "var(--radius-control)",
  cursor: "pointer",
  minWidth: "330px",
  maxWidth: "100%",
  height: "40px",
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 12px",
  transition: theme.transitions.create(["border-color", "box-shadow"], {
    duration: theme.transitions.duration.shorter,
  }),
  [theme.breakpoints.down("sm")]: {
    minWidth: "230px",
  },
}));

const DropdownPaper = styled(Paper)(({ theme }) => ({
  borderRadius: "var(--radius-container)",
  boxShadow: "var(--shadow-floating)",
  width: "100%",
  minWidth: "330px",
  marginTop: "8px",
  overflow: "hidden",
  padding: 0,
  transition: "box-shadow 0.3s ease",
  [theme.breakpoints.down("sm")]: {
    minWidth: "230px",
  },
}));

const DropdownHeader = styled(Box)(() => ({
  display: "flex",
  alignItems: "self-end",
  justifyContent: "space-between",
  padding: "16px 18px 4px 12px",
}));

const FilterChip = styled(Chip as any)<FilterChipStyleProps>(
  ({ theme, selected, colorCode }) => ({
    minWidth: 90,
    padding: "0px 5px",
    height: 28,
    borderRadius: "var(--radius-control)",
    fontSize: ".7rem",
    margin: "3px",
    cursor: "pointer",
    transition: "all 0.2s ease-in-out",
    backgroundColor: selected
      ? colorCode || theme.palette.primary.main
      : "transparent",
    color: selected
      ? "var(--palette-primary-contrastText)"
      : "var(--palette-text-primary)",
    border: `1px solid ${colorCode || theme.palette.primary.main}`,
    "&:hover": {
      backgroundColor: selected
        ? colorCode || theme.palette.primary.main
        : `${colorCode || theme.palette.primary.main}20`,
      transform: "translateY(-1px)",
    },
    "& .MuiChip-label": {
      padding: "0 0px",
      textOverflow: "ellipsis",
      overflow: "hidden",
      whiteSpace: "nowrap",
      textAlign: "center",
    },
    [theme.breakpoints.down("sm")]: {
      width: 80,
      height: 28,
      margin: "0.5px !important",
    },
  })
);

const ChannelIcon = styled("img")(() => ({
  width: "15px",
  height: "18px",
  marginRight: "4px !important",
  objectFit: "contain",
}));

const FilterDropdown = forwardRef<FilterDropdownRef, FilterDropdownProps>(
  (
    {
      channelsList = [],
      tagsList = [],
      onApplyFilters,
      filters = {},
      isLoading = false,
    },
    ref
  ) => {
    const filterChannels = filters.channels || EMPTY_CHANNELS;
    const filterTags = filters.tags || EMPTY_TAGS;
    const [open, setOpen] = useState(false);
    const anchorRef = useRef<HTMLDivElement | null>(null);
    // Local state for filter selections
    const [selectedChannels, setSelectedChannels] =
      useState<ChannelSelection[]>(filterChannels);
    const [selectedTags, setSelectedTags] = useState<FilterItem[]>(filterTags);

    // Sync local state with filters prop when filters change (handle case for tab type change)
    useEffect(() => {
      if (!filterChannels.length && !filterTags.length) {
        setSelectedChannels([]);
        setSelectedTags([]);
      }
    }, [filterChannels, filterTags]);

    // Toggle channel by name
    const handleChannelSelect = (
      channel: FilterItem,
      isRemoveChannelFilter = false
    ) => {
      setSelectedChannels((prev) => {
        // Step 1: If remove filter triggered and channel exists → remove it from the filter and update the state (for only the selected channel)
        if (isRemoveChannelFilter && channel) {
          const updatedChannelFilters = filterChannels.filter((item) =>
            isFilterItem(item) ? item.id !== channel.id : item !== channel.id
          );
          const updatedChannels = prev.filter(
            (item) => isFilterItem(item) && item.name !== channel.name
          );

          onApplyFilters("channels", updatedChannelFilters);
          return updatedChannels;
        }
        // Step 2: If channel already exists but is NOT present in active filters → remove it
        const exists = prev.some((item) =>
          isFilterItem(item) ? item.id === channel.id : item === channel.id
        );
        if (exists) {
          const updatedChannels = prev.filter((item) =>
            isFilterItem(item) ? item.id !== channel.id : item !== channel.id
          );
          return updatedChannels;
        }

        // Step 3: If channel already exists → do nothing
        if (exists) {
          return prev;
        }

        // Step 4: Otherwise, add new channel
        const newChannels = [...prev, channel];
        return newChannels;
      });
    };

    const handleTagSelect = (tag: FilterItem, isRemoveTagFilter = false) => {
      setSelectedTags((prev) => {
        // Step 1: If remove filter triggered and tag exists → remove it from the filter call the apply filters function only for selected tag
        if (isRemoveTagFilter && tag) {
          const updatedTagsFilters = filterTags.filter(
            (item) => item.id !== tag.id
          );
          const updatedTags = prev.filter(
            (item) => item.tag_name !== tag.tag_name
          );
          onApplyFilters("tags", updatedTagsFilters);
          return updatedTags;
        }
        // Step 2: If tag already exists but is NOT present in active filters → remove it from the state
        const exists = prev.some((item) => item.id === tag.id);
        if (exists) {
          const updatedTags = prev.filter((item) => item.id !== tag.id);
          return updatedTags;
        }

        // Step 3: If tag already exists → do nothing
        if (exists) {
          return prev;
        }

        // Step 4: Otherwise, add new tag
        const newTags = [...prev, tag];
        return newTags;
      });
    };

    const handleRemoveSelectedFilter = (filter: FilterItem) => {
      if (filter?.tag_name) {
        // For tags, use tag_name to match
        handleTagSelect(filter, true);
      } else if (filter?.channel_name || filter?.name) {
        // For channels, ensure name property exists for handleChannelSelect
        const channelFilter = {
          ...filter,
          name: filter.name || filter.channel_name,
        };
        handleChannelSelect(channelFilter, true);
      }
    };

    useImperativeHandle(ref, () => ({
      handleRemoveSelectedFilter,
    }));

    const handleClearAll = () => {
      setSelectedChannels([]);
      setSelectedTags([]);
      if (filterChannels.length || filterTags.length) {
        onApplyFilters("channels", []);
        onApplyFilters("tags", []);
      }
    };

    // Process channels using the existing helper
    const processedChannels = useMemo(() => {
      if (!channelsList || !Array.isArray(channelsList)) {
        return [];
      }

      // Map the channels to the expected format
      const mappedChannels = channelsList.map((channel) => ({
        ...channel,
        name: channel.channel_name || channel.name,
        image: channel.image || channel.id, // Use id as fallback for image
      }));

      return processAndSortChannels(mappedChannels) as FilterItem[];
    }, [channelsList]);

    // Calculate total selected filters and applied
    const selectedFilters =
      (selectedChannels?.length ?? 0) + (selectedTags?.length ?? 0);
    const appliedFilters =
      (filterChannels.length ?? 0) + (filterTags.length ?? 0);

    // Check if both channels and tags are empty
    const hasChannels = processedChannels && processedChannels.length > 0;
    const hasTags = tagsList && tagsList.length > 0;
    const hasNoData = !hasChannels && !hasTags;

    const handleToggle = () => {
      setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event: MouseEvent | TouchEvent) => {
      if (
        anchorRef.current &&
        event.target instanceof Node &&
        anchorRef.current.contains(event.target)
      ) {
        return;
      }
      setSelectedChannels(filterChannels);
      setSelectedTags(filterTags);
      setOpen(false);
    };

    const handleApplyFilters = () => {
      if (onApplyFilters) onApplyFilters("channels", selectedChannels);
      if (onApplyFilters) onApplyFilters("tags", selectedTags);
      setOpen(false);
    };

    const NoFilterMessage = ({ message }: { message: string }) => {
      return (
        <Alert
          icon={<InfoOutlinedIcon fontSize="small" />}
          severity="info"
          variant="outlined"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              variant="body2"
              fontStyle="italic"
              className="fw-sb"
              fontWeight="medium"
            >
              {message}
            </Typography>
          </Stack>
        </Alert>
      );
    };
    return (
      <div className="w-100">
        <FilterInput
          className="w-100"
          ref={anchorRef}
          onClick={handleToggle}
          aria-controls={open ? "filter-menu" : undefined}
          aria-haspopup="true"
          sx={{
            backgroundColor: "background.paper",
            color: "text.primary",
            border: "1px solid var(--neutral-500)",
            ...(open && FOCUS_BORDER),
            "&:hover": {
              backgroundColor: "background.paper",
              color: "text.primary",
              borderColor: open ? "var(--primary-base)" : "var(--neutral-600)",
            },
            "& .MuiSvgIcon-root": {
              color: "text.primary",
            },
          }}
        >
          <Box display="flex" alignItems="center" gap="4px">
            <Typography variant="caption" className="fw-sb">
              Filters
            </Typography>
            {appliedFilters > 0 && (
              <Typography variant="caption" className="fw-sb">
                ({appliedFilters})
              </Typography>
            )}
          </Box>
          <ArrowDropDownIcon
            sx={{
              fontSize: "21px",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease-in-out",
            }}
          />
        </FilterInput>
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          transition
          style={{ zIndex: 10 }}
          modifiers={[
            {
              name: "widthMatch",
              enabled: true,
              phase: "beforeWrite",
              requires: ["computeStyles"],
              fn: ({ state }: any) => {
                state.styles.popper.width = `${state.rects.reference.width}px`;
              },
            },
          ]}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={200}>
              <DropdownPaper>
                <ClickAwayListener onClickAway={handleClose}>
                  <Box>
                    <DropdownHeader>
                      {!hasNoData && (
                        <>
                          <Typography variant="body2" className="fw-b">
                            Filters
                          </Typography>
                          <Button
                            variant="contained"
                            size="small"
                            disabled={selectedFilters === 0}
                            onClick={handleClearAll}
                            sx={{
                              "&.Mui-disabled": {
                                backgroundColor:
                                  "color-mix(in srgb, var(--palette-common-black) 12%, transparent) !important",
                                color:
                                  "color-mix(in srgb, var(--palette-common-black) 26%, transparent) !important",
                                pointerEvents: "none",
                              },
                            }}
                          >
                            <Typography variant="caption" className="title">
                              Clear Selections
                            </Typography>
                          </Button>
                        </>
                      )}
                    </DropdownHeader>

                    <Box style={{ padding: "4px 12px 3px 12px" }}>
                      {hasNoData ? (
                        <Box textAlign="center" py={5} px={5}>
                          {isLoading ? (
                            <Stack
                              direction="column"
                              spacing={2}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <CircularProgress size={32} />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                fontStyle="italic"
                              >
                                Loading Filter Options...
                              </Typography>
                            </Stack>
                          ) : (
                            <NoFilterMessage message="No filter options available" />
                          )}
                        </Box>
                      ) : (
                        <>
                          {hasChannels && (
                            <>
                              {/* Channels Section */}
                              <Typography
                                variant="caption"
                                className="fw-sb mb-4"
                              >
                                Channels
                              </Typography>
                              <Grid container spacing={1} className="mt-2 mb-1">
                                {processedChannels &&
                                processedChannels.length > 0 ? (
                                  processedChannels.map((channel) => {
                                    const isSelected = selectedChannels.some(
                                      (selectedChannel) =>
                                        typeof selectedChannel === "object"
                                          ? selectedChannel.id === channel.id
                                          : selectedChannel === channel.id
                                    );
                                    const colorCode = getChannelColor(
                                      channel?.name
                                    );

                                    return (
                                      <Grid
                                        item
                                        xs={4}
                                        key={channel.id}
                                        sx={{
                                          display: "flex",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <FilterChip
                                          title={channel.name}
                                          icon={
                                            <ChannelIcon
                                              src={String(channel.image || "")}
                                              alt={String(channel.name || "")}
                                            />
                                          }
                                          label={channel.name}
                                          selected={isSelected}
                                          colorCode={colorCode}
                                          onClick={() =>
                                            handleChannelSelect({
                                              ...channel,
                                              colorCode,
                                            })
                                          }
                                        />
                                      </Grid>
                                    );
                                  })
                                ) : (
                                  <Grid item xs={12} className="mb-2">
                                    <NoFilterMessage message="No channels available" />
                                  </Grid>
                                )}
                              </Grid>
                            </>
                          )}

                          {/* Tags Section */}
                          {hasTags && (
                            <>
                              <Typography variant="caption" className="fw-sb">
                                Tags
                              </Typography>
                              <Grid container spacing={1} className="mt-2 mb-1">
                                {tagsList && tagsList.length > 0 ? (
                                  tagsList.map((tag) => {
                                    const isSelected = selectedTags.some(
                                      (selectedTag) => selectedTag.id === tag.id
                                    );

                                    const colorCode = tag?.color_code;

                                    return (
                                      <Grid
                                        item
                                        xs={4}
                                        key={tag.id}
                                        sx={{
                                          display: "flex",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <Box
                                          title={tag.tag_name || tag.name}
                                          onClick={() =>
                                            handleTagSelect({
                                              ...tag,
                                              colorCode,
                                            })
                                          }
                                          sx={{ cursor: "pointer" }}
                                        >
                                          <TagChip
                                            tag={{
                                              ...tag,
                                              image: tag.image
                                                ? String(tag.image)
                                                : undefined,
                                            }}
                                            customTagStyles={{
                                              height: "28px",
                                              fontSize: ".7rem",
                                              margin: "3px",
                                              borderRadius:
                                                "var(--radius-control)",
                                              fontWeight: 400,
                                              cursor: "pointer",
                                              backgroundColor: isSelected
                                                ? colorCode
                                                : `${colorCode}20`,
                                              color: isSelected
                                                ? "var(--neutral-white)"
                                                : tag.color_code,
                                            }}
                                          />
                                        </Box>
                                      </Grid>
                                    );
                                  })
                                ) : (
                                  <Grid item xs={12}>
                                    <NoFilterMessage message="No tags available" />
                                  </Grid>
                                )}
                              </Grid>
                            </>
                          )}
                        </>
                      )}
                      {!hasNoData && (
                        <>
                          <Box sx={{ padding: "16px 5px 12px 4px" }}>
                            <Button
                              variant="contained"
                              fullWidth
                              size="large"
                              className="title p-1"
                              onClick={handleApplyFilters}
                              disabled={
                                selectedFilters === 0 &&
                                appliedFilters === selectedFilters
                              }
                            >
                              Apply Filters
                            </Button>
                          </Box>
                        </>
                      )}
                    </Box>
                  </Box>
                </ClickAwayListener>
              </DropdownPaper>
            </Fade>
          )}
        </Popper>
      </div>
    );
  }
);

export default FilterDropdown;
